<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$viewAccess($route.fullPath)">
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-clock-alert</v-icon>
                Missing Punch/Reconciliation Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport" ref="filterForm">
                  <v-row class="py-3 px-3" align="end">
                    <v-col cols="12" sm="6" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.from_date"
                          label="From Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          required
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.to_date"
                          label="To Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          required
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-autocomplete
                          v-model="filters.employee_id"
                          :items="employees"
                          :item-title="item => `${item.name}-(${item.code})` ?? 'N/A'"
                          item-value="id"
                          label="Employee (Optional)"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          clearable
                          hide-details="auto"
                          :loading="employeesLoading"
                          placeholder="Search employee"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" sm="6" md="3" class="d-flex align-end">
                      <v-btn
                          type="submit"
                          color="primary"
                          variant="flat"
                          :loading="loading"
                          prepend-icon="mdi-magnify"
                          density="compact"
                          height="40"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Report Table -->
          <v-expansion-panels v-model="reportPanel" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title
                  color="blue-grey-lighten-4"
                  class="panel-header"
                  expand-icon="mdi-chevron-down"
              >
                <v-icon class="me-2">mdi-table</v-icon>
                Report Data
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                        v-model="search"
                        append-inner-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        density="compact"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        class="pl-2"
                        clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                          v-if="reportData.length > 0"
                          variant="outlined"
                          density="compact"
                          size="x-small"
                          divided
                      >
                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="success"
                            @click="exportToExcel"
                            :disabled="!reportData.length"
                        >EXCEL</v-btn
                        >
                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="red"
                            @click="exportToPDF"
                            :disabled="!reportData.length"
                        >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-progress-linear
                    v-if="loading"
                    indeterminate
                    color="primary"
                    class="mb-4"
                />

                <v-alert
                    v-if="!loading && filteredReportData.length === 0"
                    type="info"
                    variant="tonal"
                >
                  No reconciliation records found for the selected criteria.
                </v-alert>

                <div v-if="filteredReportData.length">
                  <v-table density="comfortable" class="text-caption table-bordered">
                    <thead>
                    <tr>
                      <th># SL</th>
                      <th>Date</th>
                      <th>Employee Code</th>
                      <th>Employee Name</th>
                      <th>Department</th>
                      <th>Designation</th>
                      <th>Actual In</th>
                      <th>In Remarks</th>
                      <th>Actual Out</th>
                      <th>Out Remarks</th>
                      <th>Status</th>
                      <th>Approved By</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(row, index) in filteredReportData" :key="`${row.employee?.id || row.employee?.code || ''}-${row.date}-${index}`">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatDateView(row.date) }}</td>
                      <td>{{ row.employee?.code || row.employee?.employee_code || '-' }}</td>
                      <td class="font-weight-medium">{{ row.employee?.name || '-' }}</td>
                      <td>{{ row.employee?.department?.name || '-' }}</td>
                      <td>{{ row.employee?.designation?.name || '-' }}</td>
                      <td>{{ formatTime(row.actual_in) }}</td>
                      <td>{{ row.in_remarks || '-' }}</td>
                      <td>{{ formatTime(row.actual_out) }}</td>
                      <td>{{ row.out_remarks || '-' }}</td>
                      <td>
                        <v-chip
                            size="small"
                            color="success"
                            variant="text"
                        >
                          {{ row.approval_status || 'Approved' }}
                        </v-chip>
                      </td>
                      <td>{{ row.approved_by.full_name || '-' }}</td>
                    </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";

const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

// Loading states
const loading = ref(false)
const employeesLoading = ref(false)
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

// Filters
const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

// Data
const reportData = ref([])
const employees = ref([])

const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code || item.code ? ` - ${item.employee_code || item.code}` : ''}`
}

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-') return '-'

  // Handle time strings like "08:30:00" or "08:30"
  const timeParts = timeStr.split(':')
  if (timeParts.length < 2) return timeStr

  const hours = parseInt(timeParts[0], 10)
  const minutes = timeParts[1]

  if (isNaN(hours)) return timeStr

  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

  return `${displayHours}:${minutes} ${period}`
}

// Filtered report data based on search
const filteredReportData = computed(() => {
  if (!search.value) {
    return reportData.value
  }

  const searchLower = search.value.toLowerCase()
  return reportData.value.filter(row => {
    const employeeCode = ((row.employee?.code || row.employee?.employee_code) || '').toLowerCase()
    const employeeName = (row.employee?.name || '').toLowerCase()
    const department = (row.employee?.department?.name || '').toLowerCase()
    const designation = (row.employee?.designation?.name || '').toLowerCase()
    const date = formatDateView(row.date).toLowerCase()
    return employeeCode.includes(searchLower) ||
        employeeName.includes(searchLower) ||
        department.includes(searchLower) ||
        designation.includes(searchLower) ||
        date.includes(searchLower)
  })
})

const setDefaultDates = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  filters.from_date = firstDay
  filters.to_date = lastDay
}

// Methods
const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const loadReport = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.error('Please select both From Date and To Date')
    return
  }

  loading.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id || undefined
    }

    const response = await store.dispatch('getAttendanceReconciliationReport', params)

    if (response.success) {
      reportData.value = response.data || []
      if (reportData.value.length === 0) {
        toast.info('No reconciliation records found for the selected criteria')
      }
    } else {
      toast.error(response.message || 'Failed to load report')
      reportData.value = []
    }
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || 'Failed to load report')
    reportData.value = []
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  setDefaultDates()
  filters.employee_id = null
  reportData.value = []
}

// Export to Excel
const exportToExcel = () => {
  if (!reportData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Missing Punch/Reconciliation Report'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`]
  ]

  if (filters.employee_id) {
    const selectedEmployee = employees.value.find(e => e.id === filters.employee_id)
    if (selectedEmployee) {
      data.push([`Employee: ${selectedEmployee.name || ''}${selectedEmployee.employee_code || selectedEmployee.code ? ` (${selectedEmployee.employee_code || selectedEmployee.code})` : ''}`])
    }
  }

  data.push([], ['# SL', 'Date', 'Employee Code', 'Employee Name', 'Department', 'Designation', 'Actual In', 'In Remarks', 'Actual Out', 'Out Remarks', 'Status', 'Approved By'], [])

  // Add data rows
  reportData.value.forEach((row, index) => {
    data.push([
      index + 1,
      formatDateView(row.date),
      row.employee?.code || row.employee?.employee_code || '-',
      row.employee?.name || '-',
      row.employee?.department?.name || '-',
      row.employee?.designation?.name || '-',
      formatTime(row.actual_in) || '-',
      row.in_remarks || '-',
      formatTime(row.actual_out) || '-',
      row.out_remarks || '-',
      row.approval_status || 'Approved',
      row.updated_by?.full_name || row.created_by?.full_name || '-'
    ])
  })

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'missing_punch_reconciliation_report_' + proxy.$fileDate())
  }
}

/*
|--------------------------------------------------------------------------
| Export PDF
|--------------------------------------------------------------------------
|
| Header and footer are table rows exactly like
| the attendance summary report print structure.
|
*/

const exportToPDF = async () => {

  if (!filteredReportData.value.length) {

    toast.warning(
        'No data to export'
    )

    return
  }


  const company =
      getCurrentCompany.value

  const fileServerUrl =
      getFileServerUrl.value


  const selectedEmployee =
      filters.employee_id
          ? employees.value.find(
              e => e.id === filters.employee_id
          )
          : null


  const printWindow = window.open(
      '',
      '_blank',
      `width=${screen.width}, height=${screen.height}`
  )


  const printContent = `

    <!DOCTYPE html>

    <html>

      <head>

        <meta charset="UTF-8">

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        >

        <meta
          http-equiv="X-UA-Compatible"
          content="ie=edge"
        >

        <title>
          Missing Punch/Reconciliation Report
        </title>


        <link
          href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css"
          rel="stylesheet"
        >

        <style>

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          @page {
            size: A4 landscape;
            margin: 0;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: auto;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
            background: #fff;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .report-container {
            width: 100%;
          }

          table.master-table {
            width: 100%;
            border-collapse: collapse;
          }

          .company-info {
            flex: 1;
            min-width: 0;
          }

          .company-name {
            font-size: 13px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 4px;
          }

          .company-text {
            font-size: 11px;
            margin: 2px 0;
            line-height: 1.35;
          }

          .logo-container {
            width: 160px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
          }

          .logo-container img {
            max-height: 75px;
            max-width: 150px;
            object-fit: contain;
          }

          .header-row td {
            padding: 15px 32px 10px 32px;
            border-bottom: 2px solid #eee;
          }

          .footer-row td {
            padding: 8px 32px 10px 32px;
            border-top: 1px solid #eee;
          }

          .invoice-footer {
            margin-top: auto;
            position: fixed;
            left: 0;
            bottom: 0px;
            width: 100%;
          }

          .invoice-footer p {
            font-size: 11px;
            font-weight: 500;
            margin: 3px 0;
            display: flex;
            align-items: center;
            gap: 4px;
            line-height: 1.25;
          }

          .report-title {
            font-size: 16px;
            text-align: center;
            text-decoration: underline;
            font-weight: bold;
            margin: 10px 0 15px 0;
          }

          .report-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
            font-size: 12px;
          }

          .report-info-left {
            text-align: left;
          }

          .report-info-right {
            text-align: right;
          }

          .report-info p {
            margin: 3px 0;
          }

          table.attendance-table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
            font-size: 10px;
            background: #fff;
          }

          .attendance-table thead {
            display: table-header-group;
          }


          .attendance-table tfoot {
            display: table-footer-group;
          }

          .attendance-table th,
          .attendance-table td {
            border: 1px solid #000;
            padding: 5px 4px;
            vertical-align: middle;
          }

          .attendance-table th {
            background: #e0e0e0;
            text-align: center;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .attendance-table td {
            text-align: left;
          }


          .text-center {
            text-align: center;
          }

          .text-right {
            text-align: right;
          }

          .attendance-table tr {
            page-break-inside: avoid;
          }

          .attendance-table {
            page-break-inside: auto;
          }


          .attendance-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          @media print {
            body {
              margin: 0;
              padding: 0;
            }

            .header-row {
              display: table-header-group;
            }

            .footer-row {
              display: table-footer-group;
            }

            .attendance-table thead {
              display: table-header-group;
            }

            .attendance-table tr {
              page-break-inside: avoid;
            }

          }

        </style>

      </head>


      <body>

        <div class="report-container">
          <table class="master-table">
            <thead>
              <tr>
                <td colspan="100%">
                  <div>
                    <div style="display: flex; justify-content: flex-end; align-items: flex-start; padding-bottom: 10px; margin-bottom: 16px;">
                      <img
                        src="${fileServerUrl}${company.logo}"
                        class="logo-img"
                        alt="Logo"
                        style="max-height: 80px;"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </thead>

            <tbody>
              <tr class="body-row">
                <td>

                  <div class="report-title">
                    MISSING PUNCH/RECONCILIATION REPORT
                  </div>


                  <div class="report-info">
                    <div class="report-info-left">
                      <p>
                        <strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)} </p>

                      ${selectedEmployee
      ? `
                            <p>
                              <strong>Employee:</strong>
                              ${selectedEmployee.name || ''}
                              ${ (selectedEmployee.employee_code || selectedEmployee.code)
          ? ` (${selectedEmployee.employee_code || selectedEmployee.code})`
          : ''
      }
                            </p>
                          `
      : ''
  }

                    </div>

                    <div class="report-info-right">
                      <p><strong>Total Records:</strong>${filteredReportData.value.length}</p>
                    </div>

                  </div>

                  <table class="attendance-table">
                    <thead>
                      <tr>
                        <th>SN</th>
                        <th>Date</th>
                        <th>Employee Code</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        <th>Designation</th>
                        <th>Actual In</th>
                        <th>In Remarks</th>
                        <th>Actual Out</th>
                        <th>Out Remarks</th>
                        <th>Status</th>
                        <th>Approved By</th>
                      </tr>
                    </thead>

                    <tbody>
                      ${filteredReportData.value.map((row, index) => `
                        <tr>
                          <td class="text-center">${index + 1}</td>
                          <td>${formatDateView(row.date)}</td>
                          <td>${row.employee?.code || row.employee?.employee_code || '-'}</td>
                          <td>${row.employee?.name || '-'}</td>
                          <td>${row.employee?.department?.name || '-'}</td>
                          <td>${row.employee?.designation?.name || '-'}</td>
                          <td>${formatTime(row.actual_in) || '-'}</td>
                          <td>${row.in_remarks || '-'}</td>
                          <td>${formatTime(row.actual_out) || '-'}</td>
                          <td>${row.out_remarks || '-'}</td>
                          <td>${row.approval_status || 'Approved'}</td>
                          <td>${row.updated_by?.full_name || row.created_by?.full_name || '-'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>

                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colspan="100%" style="border:none">
                  <div style="width:100%;height: 100px;">&nbsp;</div>
                </td>
              </tr>
              <tr class="footer-row">
                <td>

                  <div class="invoice-footer" style="margin-left:10px; margin-bottom:10px; color: #343591;">
                <p style="font-size: 13px; font-weight: 500; display: flex; align-items: center; gap:4px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7" />
                  </svg>

                  <span>${company?.address}</span>
                </p>

                <p style="font-size: 13px; font-weight: 500; display: flex; align-items: center; gap:4px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24c1.12.37 2.33.57 3.57.57c.55 0 1 .45 1 1V20c0 .55-.45 1-1 1c-9.39 0-17-7.61-17-17c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1c0 1.25.2 2.45.57 3.57c.11.35.03.74-.25 1.02z" />
                  </svg>

                  <strong>Phone: </strong>  +880 9610-993393, +880 2 222288648, www.remexingredient.com | www.remexcorp.com
                </p>
              </div>
                </td>
              </tr>
            </tfoot>
          </table>

        </div>

      </body>

    </html>
  `


  printWindow.document.write(
      printContent
  )

  printWindow.document.close()

  printWindow.focus()

  await new Promise(resolve =>
      setTimeout(resolve, 700)
  )

  printWindow.print()
  printWindow.close()
}

// Initialize
onMounted(async () => {
  await loadEmployees()
  setDefaultDates()
})
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.gap-2 {
  gap: 8px;
}

.panel-header {
  font-weight: 600;
}
</style>